import { useCallback, useEffect, useState } from 'react';
import { describeError } from '../../services/errorMessage';
import { fetchMitarbeiter, fetchTeilnahmen, fetchUnterweisungen } from '../../services/sifa/sifaService';
import { SifaMitarbeiter, SifaTeilnahme, SifaUnterweisung } from '../../services/sifa/types';
import { sortiereMitarbeiter } from './mitarbeiterModulLogic';

/**
 * Mitarbeiterstamm eines SiFa-Projekts samt Teilnahmen (Historie, Ampel) und
 * Kurstiteln. Zusammengefuehrte Dubletten (`zusammengefuehrtIn`) bleiben
 * draussen — sie sind in ihrer Zielzeile aufgegangen.
 */
export const useMitarbeiter = (projectId: string | null | undefined) => {
  const [mitarbeiter, setMitarbeiter] = useState<SifaMitarbeiter[]>([]);
  const [teilnahmen, setTeilnahmen] = useState<SifaTeilnahme[]>([]);
  const [unterweisungen, setUnterweisungen] = useState<SifaUnterweisung[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const neuLaden = useCallback(async () => {
    if (!projectId) {
      setMitarbeiter([]); setTeilnahmen([]); setUnterweisungen([]); setLaden(false);
      return;
    }
    setLaden(true);
    setFehler(null);
    try {
      const [m, t, u] = await Promise.all([
        fetchMitarbeiter(projectId),
        fetchTeilnahmen({ projectId }),
        fetchUnterweisungen(projectId),
      ]);
      setMitarbeiter(sortiereMitarbeiter(m.filter((x) => !x.zusammengefuehrtIn)));
      setTeilnahmen(t);
      setUnterweisungen(u);
    } catch (e) {
      setFehler(describeError(e));
    } finally {
      setLaden(false);
    }
  }, [projectId]);

  useEffect(() => { void neuLaden(); }, [neuLaden]);

  const uebernehmen = useCallback((geaendert: SifaMitarbeiter) => {
    setMitarbeiter((prev) => sortiereMitarbeiter([...prev.filter((m) => m.id !== geaendert.id), geaendert]));
  }, []);

  const uebernehmeViele = useCallback((neue: SifaMitarbeiter[]) => {
    setMitarbeiter((prev) => {
      const ids = new Set(neue.map((n) => n.id));
      return sortiereMitarbeiter([...prev.filter((m) => !ids.has(m.id)), ...neue]);
    });
  }, []);

  const entfernen = useCallback((id: string) => {
    setMitarbeiter((prev) => prev.filter((m) => m.id !== id));
  }, []);

  return { mitarbeiter, teilnahmen, unterweisungen, laden, fehler, neuLaden, uebernehmen, uebernehmeViele, entfernen };
};
